import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  forwardRef,
  OnDestroy,
  Injector,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  ReactiveFormsModule,
  NgControl,
} from '@angular/forms';
import { TextAreaModule } from '@progress/kendo-angular-inputs';
import { Subject } from 'rxjs';

interface TextareaConfig {
  label?: string;
  placeholder?: string;
  rows?: number;
  cols?: number;
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  maxLength?: number;
  minLength?: number;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  hint?: string;
  showErrors?: boolean;
}

interface ValidationConfig {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  errorMessages?: {
    [key: string]: string;
  };
}

@Component({
  selector: 'app-textarea',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TextAreaModule],
  template: `
    <div class="form-field textarea-field" [class.has-error]="showError">
      <!-- Label -->
      <label *ngIf="config?.label" class="form-label">
        {{ config?.label }}
        <span *ngIf="config?.required || validation?.required" class="required-indicator">*</span>
      </label>

      <!-- Textarea - Use value binding instead of ngModel -->
      <kendo-textarea
        [value]="value"
        [placeholder]="config?.placeholder || ''"
        [rows]="config?.rows || 3"
        [disabled]="isDisabled"
        [readonly]="config?.readonly || false"
        [resizable]="config?.resize || 'vertical'"
        (valueChange)="onValueChange($event)"
        (blur)="onBlur()"
      >
      </kendo-textarea>

      <!-- Character counter -->
      <div
        *ngIf="maxLengthValue && value"
        class="character-counter"
        [class.warning]="value && value.length > maxLengthValue * 0.9"
      >
        {{ value.length || 0 }} / {{ maxLengthValue }}
      </div>

      <!-- Hint text -->
      <div *ngIf="config?.hint && !showError" class="field-hint">
        {{ config?.hint }}
      </div>

      <!-- Error messages -->
      <div *ngIf="showError" class="error-text">
        <span class="k-icon k-i-warning"></span>
        {{ getErrorMessage() }}
      </div>
    </div>
  `,
  styles: [``],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TextareaComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TextareaComponent),
      multi: true,
    },
  ],
})
export class TextareaComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {
  @Input() config?: TextareaConfig;
  @Input() validation?: ValidationConfig;

  @Output() onChange = new EventEmitter<string>();
  @Output() onBlurEvent = new EventEmitter<void>();

  value: string = '';
  isDisabled: boolean = false;
  errors: ValidationErrors | null = null;
  touched: boolean = false;

  public ngControl: NgControl | null = null;

  private readonly destroy$ = new Subject<void>();
  private onTouchedCallback: () => void = () => {};
  private onChangeCallback: (_: any) => void = () => {};

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly injector: Injector
  ) {}

  ngOnInit() {
    this.isDisabled = this.config?.disabled ?? false;

    this.ngControl = this.injector.get(NgControl, null);

    if (this.config && this.config.showErrors === undefined) {
      this.config.showErrors = true;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showError(): boolean {
    // Check if showErrors is disabled
    if (this.config?.showErrors === false) {
      return false;
    }

    // Check if there are any errors
    const hasErrors = !!(this.errors || this.ngControl?.control?.errors);
    if (!hasErrors) {
      return false;
    }

    const isTouched =
      this.touched || this.ngControl?.control?.touched || this.ngControl?.control?.dirty;

    return !!isTouched;
  }

  get maxLengthValue(): number | null {
    return this.config?.maxLength ?? this.validation?.maxLength ?? null;
  }

  getErrorMessage(): string {
    if (!this.showError) return '';

    const errors = this.ngControl?.control?.errors || this.errors;
    if (!errors) return '';

    const errorMessages = this.validation?.errorMessages ?? {};
    const firstErrorKey = Object.keys(errors)[0];

    return errorMessages[firstErrorKey] || 'This field is invalid';
  }

  onValueChange(value: string): void {
    this.value = value ?? '';

    // CRITICAL: Notify Angular Forms
    this.onChangeCallback(this.value);

    // Emit for parent components
    this.onChange.emit(this.value);

    // Trigger validation on the actual control
    if (this.ngControl?.control) {
      this.ngControl.control.updateValueAndValidity();
    }

    this.cdr.markForCheck();
  }

  onBlur(): void {
    this.touched = true;
    this.onTouchedCallback();
    this.onBlurEvent.emit();
    this.cdr.markForCheck();
  }

  // ControlValueAccessor implementation
  writeValue(value: any): void {
    this.value = value ?? '';
    this.cdr.markForCheck();
  }

  registerOnChange(fn: any): void {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouchedCallback = fn;
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    const errors: ValidationErrors = {};

    // Handle required validation
    this.validateRequired(value, errors);

    // Handle other validations if applicable
    if (value && this.validation) {
      const strValue = value.toString();

      this.validateLength(strValue, errors);
      this.validatePattern(strValue, errors);
    }

    this.errors = Object.keys(errors).length ? errors : null;
    return this.errors;
  }

  private validateRequired(value: any, errors: ValidationErrors): void {
    const isRequired = this.config?.required || this.validation?.required;
    if (isRequired && (!value || value.toString().trim() === '')) {
      errors['required'] = true;
    }
  }

  private validateLength(strValue: string, errors: ValidationErrors): void {
    const { minLength, maxLength } = this.validation!;

    if (minLength && strValue.length < minLength) {
      errors['minLength'] = { requiredLength: minLength, actualLength: strValue.length };
    }

    if (maxLength && strValue.length > maxLength) {
      errors['maxLength'] = { requiredLength: maxLength, actualLength: strValue.length };
    }
  }

  private validatePattern(strValue: string, errors: ValidationErrors): void {
    const patternStr = this.validation?.pattern;
    if (!patternStr) return;

    try {
      const pattern = new RegExp(patternStr);
      if (!pattern.test(strValue)) {
        errors['pattern'] = true;
      }
    } catch {
      console.error('Invalid regex pattern:', patternStr);
    }
  }
}
